import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useArchivedApiCli, useUnarchiveCli } from '../api';
import type { ArchivedCliRow } from '../types';

export function ArchivedApiCliListPage() {
  const query = useArchivedApiCli();
  const unarchive = useUnarchiveCli();
  const [pending, setPending] = useState<ArchivedCliRow | null>(null);

  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<ArchivedCliRow>[]>(
    () => [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
      {
        id: 'actions',
        header: 'Settings',
        size: 150,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={() => setPending(row.original)}>
            <ArchiveRestore className="h-4 w-4" />
            Unarchive
          </Button>
        ),
      },
    ],
    [],
  );

  const confirmUnarchive = () => {
    if (!pending) return;
    unarchive.mutate(
      { cliId: pending.cliId, allocated: 1 },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Unarchived ${pending.cli}`);
          setPending(null);
        },
        onError: () => toast.error('Could not unarchive the CLI.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Archive"
        description="API CLIs that have been archived. Restore them to make them assignable again."
      />

      <DataTable<ArchivedCliRow, unknown>
        tableId="cli:archived-api:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search archived API CLIs…"
        emptyTitle="No archived API CLIs"
        emptyDescription="Nothing has been archived yet."
        getRowId={(row) => String(row.cliId)}
        exportFilename="archived-api-cli.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Unarchive this CLI?"
        description={
          <>
            <span className="font-medium text-foreground">{pending?.cli}</span> will become
            available again for allocation.
          </>
        }
        confirmLabel="Unarchive"
        loading={unarchive.isPending}
        onConfirm={confirmUnarchive}
      />
    </FillPage>
  );
}
